import { useQuery } from "@tanstack/react-query";
import { vehicleOpsApi, type LicenseAlert, type FleetHealthItem } from "@/services/vehicleOps";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card, MetricsRow } from "@/shared/ui/Card";
import { VehicleOpsTabs } from "./VehicleOpsTabs";

const usd = (c: number) => "$" + (c / 100).toLocaleString("en-US");

const LEVEL: Record<string, { row: string; badge: string; label: string }> = {
  yellow: { row: "border-amber-200 bg-amber-50", badge: "bg-amber-100 text-amber-700", label: "🟡 60 天内" },
  red: { row: "border-rose-200 bg-rose-50", badge: "bg-rose-100 text-rose-700", label: "🔴 30 天内" },
  expired: { row: "border-rose-300 bg-rose-100", badge: "bg-rose-200 text-rose-900", label: "⚫ 已过期" },
};

export function VehicleOpsOverview() {
  const q = useQuery({ queryKey: ["vehicle-ops", "overview"], queryFn: vehicleOpsApi.overview });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>车务台账总览加载失败</span>
      <button onClick={() => q.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  if (!q.data) return <LoadingState />;
  const d = q.data;
  const m = d.metrics;

  return (
    <PageShell
      title="🛠 车务台账 · 总览"
      subtitle={`在役 ${m.active_vehicles} 车 · 维修中 ${m.in_repair} · 证照预警 ${m.license_warnings} · 本月维修 + 油费`}
    >
      <VehicleOpsTabs active="overview" />
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12px] text-amber-800">
        权限：老板 + 财务 + 车务管理员全权 · OP 经理只读 · 编辑集中此处（与调度中心 mirror）
      </div>

      <MetricsRow
        items={[
          { label: "在役车", value: String(m.active_vehicles) },
          { label: "维修中", value: String(m.in_repair) },
          { label: "证照预警", value: String(m.license_warnings), hint: "60/30 双层" },
          { label: "本月维修费", value: usd(m.month_maintenance_cents) },
          { label: "本月油费", value: usd(m.month_fuel_cents) },
          { label: "累计里程", value: m.total_mileage_km.toLocaleString() + " km" },
        ]}
      />

      <Card title="🚨 证照到期预警（60/30 天双层 · 7 类证照统一 · 前端实时计算 · 无 cron）">
        {d.license_alerts.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">暂无证照预警（60 天内）</div>
        ) : (
          <div className="flex flex-col gap-2">
            {d.license_alerts.map((a, i) => <LicenseRow key={i} a={a} />)}
          </div>
        )}
      </Card>

      <Card title={`🚐 车队健康看板（${d.fleet_health.length} 车 · 健康分 = 证照 + 状态）`}>
        {d.fleet_health.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">暂无车辆</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {d.fleet_health.map((v, i) => <FleetCard key={i} v={v} />)}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="🔧 近期维修工单">
          {d.recent_maintenance.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">暂无维修工单</div>
          ) : (
            <div className="flex flex-col divide-y divide-slate-100">
              {d.recent_maintenance.map((r) => (
                <div key={r.order_number} className="flex items-center justify-between py-2.5 text-[13px]">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-900">{r.vehicle_number} · {r.maintenance_type}</div>
                    <div className="truncate text-[11px] text-slate-500">{r.order_number} · {r.vendor_name ?? "—"}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-semibold text-slate-900">{usd(r.cost_cents)}</div>
                    <div className="text-[11px] text-slate-500">{r.status}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="💸 本月费用汇总">
          <div className="flex flex-col gap-2 rounded-lg bg-slate-50 p-4 text-[13px]">
            <SumRow label="🔧 维修" value={usd(d.expense_summary.maintenance_cents)} />
            <SumRow label="⛽ 油费" value={usd(d.expense_summary.fuel_cents)} />
            <SumRow label="🛡 保险（按月分摊）" value={usd(d.expense_summary.insurance_cents)} />
            <SumRow label="💳 罚款" value={usd(d.expense_summary.fine_cents)} />
            <div className="my-1 h-px bg-slate-200" />
            <div className="flex items-center justify-between font-bold">
              <span>合计</span>
              <span className="text-base text-rose-600">{usd(d.expense_summary.total_cents)}</span>
            </div>
          </div>
          <div className="mt-2 rounded bg-slate-100 px-2.5 py-1.5 text-[11px] text-slate-500">
            💡 财务对账 / QB 同步 · <span className="font-semibold">待接财务模块</span>（financial 模块上线后接真值）
          </div>
        </Card>
      </div>
    </PageShell>
  );
}

function LicenseRow({ a }: { a: LicenseAlert }) {
  const lv = LEVEL[a.level] ?? LEVEL.yellow;
  return (
    <div className={`flex items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 ${lv.row}`}>
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold text-slate-900">{a.vehicle_number} · {a.license_type}</div>
        <div className="text-[11px] text-slate-500">到期 {a.expiry_date}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-[13px] font-bold text-slate-900">{a.days_to_expiry < 0 ? `逾期 ${-a.days_to_expiry} 天` : `${a.days_to_expiry} 天`}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${lv.badge}`}>{lv.label}</span>
      </div>
    </div>
  );
}

function FleetCard({ v }: { v: FleetHealthItem }) {
  const lv = v.alert ? LEVEL[v.alert.level] : null;
  const scoreColor = v.health_score >= 85 ? "text-emerald-600" : v.health_score >= 70 ? "text-amber-600" : "text-rose-600";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[14px] font-bold text-slate-900">{v.vehicle_number}</div>
          <div className="text-[11px] text-slate-500">{v.model}</div>
        </div>
        <span className={`text-2xl font-extrabold ${scoreColor}`}>{v.health_score}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">{v.status === "active" ? "在役" : v.status}</span>
        {v.mileage_km != null && <span>{v.mileage_km.toLocaleString()} km</span>}
      </div>
      {v.alert && lv && (
        <div className={`mt-2 rounded-lg px-2.5 py-1.5 text-[11px] ${lv.badge}`}>
          {lv.label} · {v.alert.license_type}（{v.alert.days_to_expiry < 0 ? `逾期 ${-v.alert.days_to_expiry} 天` : `${v.alert.days_to_expiry} 天`}）
        </div>
      )}
    </div>
  );
}

function SumRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-medium text-slate-700">{label}</span>
      <span className="font-bold text-rose-600">{value}</span>
    </div>
  );
}
